/**
 * A component's own undo history.
 *
 * The browser keeps one for a contenteditable, but only for the edits it made
 * itself. Everything this application writes into the DOM - a field chip, a
 * table column, a repainted cell - never reaches that stack, so Ctrl+Z would
 * step straight over the change the user just made and undo the last thing
 * they typed instead. Once any of it is ours, all of it has to be.
 *
 * A component using this supplies two things:
 *
 *   historySnapshot()    the current state, as `{key, ...}` - `key` is the
 *                        content that decides whether anything changed, so it
 *                        must not include the caret
 *   historyApply(state)  put one back
 *
 * and then calls `historyRecord()` after changing something itself, or
 * `historySchedule()` while the user is typing: consecutive keystrokes are
 * collected into one step, the way an editor's undo normally behaves.
 */

/** Quiet time that ends one typing step, in milliseconds. */
const TYPING_PAUSE = 400;

/** Steps kept before the oldest is dropped. */
const LIMIT = 200;

/**
 * Characters kept across every step together.
 *
 * A letter body can carry an embedded image as a data-uri, and two hundred
 * copies of one of those is a great deal of memory for the sake of an undo
 * nobody will reach for.
 */
const SIZE_LIMIT = 4e6;

export default {
  data() {
    return {
      historyStack: [],
      historyAt: -1,
      historyTimer: null,
      // Set while a state is being put back, so restoring cannot record.
      historyBusy: false
    };
  },
  beforeDestroy() {
    clearTimeout(this.historyTimer);
  },
  methods: {
    /** Start again from where things stand now, forgetting every step. */
    historyReset() {
      clearTimeout(this.historyTimer);
      this.historyTimer = null;
      this.historyStack = [];
      this.historyAt = -1;
      this.historyRecord();
    },
    /**
     * Write down the current state as a step of its own.
     *
     * Nothing is added when the content has not changed - only the caret is
     * refreshed, so an undo returns the user to where they actually are rather
     * than where they were when that state was first reached.
     */
    historyRecord() {
      if (this.historyBusy) return;
      clearTimeout(this.historyTimer);
      this.historyTimer = null;

      const state = this.historySnapshot();
      if (!state) return;

      const current = this.historyStack[this.historyAt];
      if (current && current.key === state.key) {
        this.$set(this.historyStack, this.historyAt, state);
        return;
      }

      // Anything that was undone is dropped: the user has taken a new branch,
      // and there is no way back to the old one.
      this.historyStack = this.historyStack.slice(0, this.historyAt + 1);
      this.historyStack.push(state);

      while (this.historyStack.length > LIMIT) this.historyStack.shift();
      let held = this.historyStack.reduce((total, s) => total + s.key.length, 0);
      while (this.historyStack.length > 2 && held > SIZE_LIMIT) {
        held -= this.historyStack.shift().key.length;
      }
      this.historyAt = this.historyStack.length - 1;
    },
    /** Typing: one step per burst, not one per keystroke. */
    historySchedule() {
      if (this.historyBusy) return;
      clearTimeout(this.historyTimer);
      this.historyTimer = setTimeout(() => this.historyRecord(), TYPING_PAUSE);
    },
    /** Close off a typing burst that has not timed out yet. */
    historyFlush() {
      if (this.historyTimer) this.historyRecord();
    },
    historyUndo() {
      // A burst still in progress is a step in its own right: without this,
      // Ctrl+Z straight after typing would throw away the burst *and* the step
      // before it.
      this.historyFlush();
      if (this.historyAt <= 0) return false;
      this.historyAt -= 1;
      return this.historyRestore();
    },
    historyRedo() {
      this.historyFlush();
      if (this.historyAt >= this.historyStack.length - 1) return false;
      this.historyAt += 1;
      return this.historyRestore();
    },
    historyRestore() {
      const state = this.historyStack[this.historyAt];
      if (!state) return false;
      this.historyBusy = true;
      try {
        this.historyApply(state);
      } finally {
        this.historyBusy = false;
      }
      return true;
    },
    /**
     * Which of undo/redo a keystroke asks for, if either.
     *
     * Read from `e.code` rather than `e.key`: on an Arabic layout the Z key
     * reports 'ئ', and the shortcut has to keep working there - which is the
     * layout most of this application is typed in.
     *
     * @param {KeyboardEvent} e
     * @returns {'undo'|'redo'|null}
     */
    historyHotkey(e) {
      if (!e || !(e.ctrlKey || e.metaKey) || e.altKey) return null;
      const code = e.code || '';
      const key = String(e.key || '').toLowerCase();
      if (code === 'KeyY' || key === 'y') return 'redo';
      if (code === 'KeyZ' || key === 'z') return e.shiftKey ? 'redo' : 'undo';
      return null;
    }
  }
};
